import React, { useEffect, useState } from "react"
import Section from "@components/section"
import ProductItem from "@components/product/item"
import Pagination from "@components/pagination/index"
import { sortByOption } from "./helpers"
import FiltersToggleButton from "@components/product/filters/toggle-button"
import ProductFiltersDesktop from "./desktop"
import ProductFiltersMobile from "./mobile"
import { trackViewItemList } from "@src/services/analytics"
import CompareAtCrates from "@src/components/compare-at-modal"

const ProductFilters = ({
  allProductGroups,
  selectedProductGroup,
  allSelectedProducts,
  params,
}) => {
  const searchParam = params.get("search")?.split(",")
  const queryParam = params.get("query")
  const hasSearchItems = Boolean(searchParam?.length)

  const productFromList = selectedProductGroup?.slug?.current
    ? `/collections/${selectedProductGroup?.slug?.current}/`
    : "/collections/"

  if (hasSearchItems) {
    allSelectedProducts = allSelectedProducts.filter(product =>
      searchParam?.includes(product?._id)
    )
  }

  const [currentPage, setCurrentPage] = useState(params.get("page") || 1)
  const [itemsPerPage, setItemsPerPage] = useState(9)
  const [sortingOptions, setSortingOptions] = useState("relevance")
  const [showCompareModal, setShowCompareModal] = useState(false);

  const sortedProducts = sortByOption(allSelectedProducts, sortingOptions)

  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage)

  if ((currentPage - 1) * itemsPerPage > sortedProducts.length) {
    setCurrentPage(totalPages)
  }

  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + +itemsPerPage

  const productsToDisplay = sortedProducts
    .map(product => {
      if (product.isBundle) {
        return {
          ...product,
          bundleProductsList: product.bundleProductsList.map(bundleProduct => {
            return {
              ...bundleProduct,
              variants: bundleProduct?.variants
                ?.filter(
                  variant =>
                    !variant.hideOnBundle ||
                    !(variant.hideOnBundle || []).some(
                      bundleProduct => bundleProduct._id == product._id
                    )
                )
                ?.filter(variant => {
                  return !product.hiddenVariants?.find(
                    hiddenVariant =>
                      hiddenVariant?.shopifyId === variant?.shopifyId
                  )
                }),
            }
          }),
        }
      }

      return product
    })
    .slice(startIndex, endIndex)

  const onPageChange = newPage => {
    setCurrentPage(newPage)
  }

  useEffect(() => {
    const impressions = productsToDisplay.flatMap((product, index) => {
      const variant = product.variants?.[0]
      const isBundle = product.isBundle

      if (isBundle) {
        const bundleProducts = product?.bundleProductsList

        return bundleProducts.map(bundleProduct => {
          const bundleVariant = bundleProduct?.variants?.[0]

          return {
            merchandise: {
              id: bundleVariant?.shopifyId,
              sku: bundleVariant?.sku,
              title: bundleVariant?.variantTitle,
              price: bundleVariant?.price,
              compareAtPrice: bundleVariant?.compareAtPrice,
              position: +index,
              list: productFromList,
              product: {
                id: bundleProduct.shopifyId,
                title: bundleProduct.title,
                category: bundleProduct.productGroups?.map(
                  group => group.title
                ),
              },
            },
          }
        })
      }

      return {
        merchandise: {
          id: variant?.shopifyId,
          sku: variant?.sku,
          title: variant?.variantTitle,
          price: variant?.price,
          compareAtPrice: variant?.compareAtPrice,
          position: +index,
          list: productFromList,
          product: {
            id: product.shopifyId,
            title: product.title,
            category: product.productGroups?.map(group => group.title),
          },
        },
      }
    })

    if (impressions.length > 0) {
      trackViewItemList(impressions)
    }
  }, [currentPage])

  return (
    <div className="flex justify-center">
      <Section settings={{ classname: "!mt-0" }}>
        <div className="grid grid-cols-1 md:grid-cols-4 md:gap-x-large w-full">
          <FiltersToggleButton
            {...{
              allProductGroups,
              selectedProductGroup,
              searchItem: hasSearchItems ? queryParam : null,
            }}
          />
          <ProductFiltersDesktop
            {...{
              allProductGroups,
              selectedProductGroup,
              sortingOptions,
              setSortingOptions,
              itemsPerPage,
              setItemsPerPage,
              searchItem: hasSearchItems ? queryParam : null,
              sortedProducts,
            }}
          />
          <ProductFiltersMobile
            {...{
              sortingOptions,
              setSortingOptions,
              itemsPerPage,
              setItemsPerPage,
              sortedProducts,
            }}
          />
          <div className="col-span-1 md:col-span-3 flex flex-col gap-y-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-y-10">
              {productsToDisplay?.map((product, index) => (
                <ProductItem
                  key={index}
                  index={index}
                  product={product}
                  productInfos={true}
                  label={"See Details"}
                  bundleLabel={"See Details"}
                  cardFlags={true}
                  fromList={productFromList}
                  setShowCompareModal={setShowCompareModal}
                />
              ))}
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
              />
            )}
          </div>
        </div>
        {showCompareModal && (
          <CompareAtCrates
            showCompareModal={showCompareModal}
            setShowCompareModal={setShowCompareModal}
          />
        )}
      </Section>
    </div>
  )
}

export default ProductFilters
